import {
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import '../../styles/modal.css';

/**
 * Modal size variants
 */
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Props for the Modal component
 */
interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Modal content */
  children: ReactNode;
  /** Size variant */
  size?: ModalSize;
  /** Whether to show close button */
  showCloseButton?: boolean;
  /** Whether clicking backdrop closes modal */
  closeOnBackdropClick?: boolean;
  /** Whether pressing Escape closes modal */
  closeOnEscape?: boolean;
  /** Footer content */
  footer?: ReactNode;
  /** Additional class for modal content */
  className?: string;
  /** Whether to use full backdrop (dims dock) */
  fullBackdrop?: boolean;
  /** ID for accessibility */
  id?: string;
}

/**
 * Close icon SVG component
 */
function CloseIcon(): JSX.Element {
  return (
    <svg
      className="modal-close-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Focus trap hook - keeps focus within modal
 */
function useFocusTrap(
  isOpen: boolean,
  modalRef: React.RefObject<HTMLDivElement | null>
): void {
  useEffect(() => {
    if (!isOpen || !modalRef.current) {
      return;
    }

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus first element when modal opens
    const previousActiveElement = document.activeElement as HTMLElement;
    firstFocusable?.focus();

    const handleTabKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      // Restore focus when modal closes
      previousActiveElement?.focus();
    };
  }, [isOpen, modalRef]);
}

/**
 * Lock body scroll when modal is open
 */
function useBodyScrollLock(isOpen: boolean): void {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    // Get scrollbar width
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Lock scroll and compensate for scrollbar
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);
}

/**
 * Modal component
 *
 * Features:
 * - Glass morphism styling
 * - Backdrop does NOT dim the dock (dock stays visible)
 * - Focus trap for accessibility
 * - Escape key to close
 * - Portal rendering to body
 * - Body scroll lock when open
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  footer,
  className = '',
  fullBackdrop = false,
  id,
}: ModalProps): JSX.Element | null {
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap
  useFocusTrap(isOpen, modalRef);

  // Body scroll lock
  useBodyScrollLock(isOpen);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return;
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    if (closeOnBackdropClick) {
      onClose();
    }
  }, [closeOnBackdropClick, onClose]);

  // Prevent click propagation from modal content
  const handleModalClick = useCallback(
    (event: React.MouseEvent): void => {
      event.stopPropagation();
    },
    []
  );

  // Handle close button keyboard
  const handleCloseKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  // Don't render if closed
  if (!isOpen) {
    return null;
  }

  // Build modal classes
  const modalClasses = [
    'modal',
    'modal--visible',
    size !== 'md' ? `modal--${size}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const backdropClasses = [
    'modal-backdrop',
    'modal-backdrop--visible',
    fullBackdrop ? 'modal-backdrop--full' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Modal ID for accessibility
  const modalId = id || 'modal';
  const titleId = `${modalId}-title`;

  const modalContent = (
    <>
      {/* Backdrop - does not cover dock by default */}
      <div
        className={backdropClasses}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={modalClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onClick={handleModalClick}
        id={modalId}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && (
              <h2 id={titleId} className="modal-title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className="modal-close"
                onClick={onClose}
                onKeyDown={handleCloseKeyDown}
                aria-label="Close modal"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">{children}</div>

        {/* Footer */}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </>
  );

  // Render in portal
  return createPortal(modalContent, document.body);
}

export default Modal;
